import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const IMGS = [
  "/images/storefront.jpg",
  "/images/panaderia.jpg",
  "/images/frutas-verduras.jpg",
  "/images/chiles.jpg",
  "/images/queso.jpg",
  "/images/chicharron.jpg",
  "/images/produce.jpg",
  "/images/candy.jpg",
  "/images/chips.jpg",
  "https://images.pexels.com/photos/35250923/pexels-photo-35250923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=900",
  "https://images.pexels.com/photos/18574183/pexels-photo-18574183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=600",
  "https://images.pexels.com/photos/35770688/pexels-photo-35770688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=900",
  "/images/accesorios-cowgirl.png",
];

const TALL_SET = new Set([1, 4, 6, 9]);

export default function Gallery() {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i - 1 + IMGS.length) % IMGS.length)),
    []
  );
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i + 1) % IMGS.length)),
    []
  );

  useEffect(() => {
    if (openIndex === null) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIndex, close, prev, next]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-[#DED3C4] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="max-w-3xl mb-12">
          <span className="text-[12px] uppercase tracking-[0.22em] text-[#C05A3A] font-medium">
            {t("gallery.eyebrow")}
          </span>
          <h2 className="font-serif-display text-[#232321] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4">
            {t("gallery.title")}
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[240px]">
          {IMGS.map((src, i) => {
            const tall = TALL_SET.has(i);
            return (
              <button
                key={src}
                data-testid={`gallery-img-${i}`}
                onClick={() => setOpenIndex(i)}
                className={`relative overflow-hidden rounded-2xl border border-[#E2D9CD] group cursor-zoom-in text-left ${
                  tall ? "row-span-2" : ""
                }`}
                aria-label={`Open photo ${i + 1}`}
              >
                <img
                  src={src}
                  alt={`Hacienda ${i + 1}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      </div>

      {openIndex !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-[#0f0f0e]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 animate-in fade-in duration-200"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-[#F9F6F0]/10 hover:bg-[#F9F6F0]/20 border border-[#F9F6F0]/20 text-[#F9F6F0] flex items-center justify-center backdrop-blur-md transition-colors"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>

          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute left-3 sm:left-8 h-12 w-12 rounded-full bg-[#F9F6F0]/10 hover:bg-[#F9F6F0]/20 border border-[#F9F6F0]/20 text-[#F9F6F0] flex items-center justify-center backdrop-blur-md transition-colors"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={1.5} />
          </button>

          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute right-3 sm:right-8 h-12 w-12 rounded-full bg-[#F9F6F0]/10 hover:bg-[#F9F6F0]/20 border border-[#F9F6F0]/20 text-[#F9F6F0] flex items-center justify-center backdrop-blur-md transition-colors"
          >
            <ChevronRight className="h-6 w-6" strokeWidth={1.5} />
          </button>

          <figure
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
          >
            <img
              src={IMGS[openIndex]}
              alt={`Hacienda photo ${openIndex + 1}`}
              data-testid="lightbox-image"
              className="max-w-[92vw] max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <figcaption className="mt-4 text-[12px] uppercase tracking-[0.22em] text-[#F9F6F0]/70">
              {openIndex + 1} / {IMGS.length}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
